import {getIntlLanguage} from '@di-shared/features/common/useInferableTranslation';

/**
 * @description CLDR 포맷정리
 * @link https://hmg.dooray.com/task/3621815477071999771/3858061152115956455
 */
const CLDR_DATETIME_FORMAT_MAP = {
  /**
   * kr: 2024년 9월 30일 월요일
   * en: Monday, September 30, 2024
   * ja: 2024年9月30日月曜日
   * fr: lundi 30 septembre 2024
   */
  full: {
    dateStyle: 'full',
  },
  /**
   * kr: 2024년 9월 30일
   * en: September 30, 2024
   * it: 30 settembre 2024
   * ja: 2024年9月30日
   * fr: 30 septembre 2024
   */
  long: {
    dateStyle: 'long',
  },
  /**
   * kr: 2024. 9. 30.
   * en: Sep 30, 2024
   * it: 30 set 2024
   * ja: 2024/09/30
   * fr: 30 sept. 2024
   */
  medium: {
    dateStyle: 'medium',
  },
  /**
   * kr: 24. 9. 30.
   * en: 9/30/24
   * it: 30/09/24
   * ja: 24/9/30
   * fr: 30/09/24
   */
  short: {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  },
  /**
   * kr: 2024년 9월
   * en: Sep 2024
   * it: set 2024
   * ja: 2024年9月
   * fr: sept. 2024
   */
  yMMM: {
    year: 'numeric',
    month: 'short',
  },
  /**
   * kr: 9월 30일
   * en: Sep 30
   * it: 30 set
   * ja: 9月30日
   * fr: 30 sept.
   */
  MMMd: {
    month: 'short',
    day: 'numeric',
  },
  /**
   * kr: 9월 30일 월요일
   * en: Monday, Sep 30
   * it: lunedì 30 set
   * ja: 9月30日月曜日
   * fr: lundi 30 sept.
   */
  MMMMEEEEd: {
    month: 'short',
    weekday: 'long',
    day: 'numeric',
  },
  /**
   * kr: 2024년 9월 30일
   * en: Sep 30, 2024
   * it: 30 set 2024
   * ja: 2024年9月30日
   * fr: 30 sept. 2024
   */
  yMMMd: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  /**
   * kr: 2024년 9월 30일 (월)
   * en: Mon, Sep 30, 2024
   * it: lun 30 set 2024
   * ja: 2024年9月30日(月)
   * fr: lun. 30 sept. 2024
   */
  yMMMEd: {
    year: 'numeric',
    month: 'short',
    weekday: 'short',
    day: 'numeric',
  },
  /**
   * kr: 2024년 9월 30일 오후 1:34
   * en: Sep 30, 2024, 1:36 PM
   * it: 30 set 2024, 1:40 PM
   * ja: 2024年9月30日 午後1:41
   * fr: 30 sept. 2024, 1:42 PM
   */
  yMMMdHM: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    hour12: true,
    minute: 'numeric',
  },
  /**
   * kr: 2024년 9월 30일 오후 1:34
   * en: Sep 30, 2024, 1:36 PM
   * it: 30 set 2024, 13:40
   * ja: 2024年9月30日 13:41
   * fr: 30 sept. 2024, 13:42
   */
  yMMMaHM: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  /**
   * kr: 9월
   * en: Sep
   * it: set
   * ja: 9月
   * fr: sept.
   */
  MMM: {
    month: 'short',
  },
  /**
   * kr: 9월
   * en: September
   * it: settembre
   * ja: 9月
   * fr: septembre
   */
  MMMM: {
    month: 'long',
  },
  /**
   * kr: 오후 1:45
   * en: 1:46 PM
   * it: 13:47
   * ja: 13:47
   * fr: 13:47
   */
  aHM: {
    hour: 'numeric',
    minute: 'numeric',
  },
  /**
   * kr: 06. 26.
   * en: 06/26
   * it: 26/06
   * ja: 06/26
   * fr: 26/06
   */
  Md: {
    month: '2-digit',
    day: '2-digit',
  },
} as const;

function useCldrDatetimeFormatter() {
  const language = getIntlLanguage();

  type DatetimeFormat = keyof typeof CLDR_DATETIME_FORMAT_MAP;

  function format(date: Date, format: DatetimeFormat) {
    return new Intl.DateTimeFormat(language, CLDR_DATETIME_FORMAT_MAP[format]).format(date);
  }

  return {
    format,
  };
}

export default useCldrDatetimeFormatter;
